import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import usePrevious from '../../../hooks/usePrevious'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import store from '../../../../store'
import { BaseWbsItemType } from '../../../../store/project'
import {
  ActionPopperAddChildButtonProps,
  ActionPopperButtonProps,
} from '../components/ActionPopper'
import {
  getAddableChildTypes,
  isAddableChildType,
  NewWbsItemRow,
} from '../projectPlanNew'
import { intl } from '../../../../i18n'
import { fromSnakeToCamel } from '../../../../utils/string'
import { WbsItemType } from '../../../../domain/entity/WbsItemEntity'
import { GridApi } from 'ag-grid-community'

export const useActionPopper = ({
  gridApi,
  wbsItemTypes,
  addRowAbove,
  addRowBelow,
  addToChildRow,
  prefetchParentBody,
  isFetchingBody,
}: {
  gridApi: GridApi | undefined
  wbsItemTypes: BaseWbsItemType
  addRowAbove: (
    type: WbsItemTypeVO,
    ticketListUuid?: string,
    targetRowUuid?: string | undefined,
    focusNewRow?: boolean
  ) => boolean | undefined
  addRowBelow: (
    type: WbsItemTypeVO,
    ticketListUuid?: string,
    targetRowUuid?: string | undefined,
    focusNewRow?: boolean
  ) => boolean | undefined
  addToChildRow: (
    type: WbsItemTypeVO,
    ticketListUuid?: string,
    targetRowUuid?: string | undefined
  ) => void
  prefetchParentBody: (targetRowUuid: string) => void
  isFetchingBody: boolean
}) => {
  const [offsetX, setOffsetX] = useState<number>(0)
  const [offsetY, setOffsetY] = useState<number>(0)
  const [anchor, setAnchor] = useState<HTMLElement | undefined>()
  const [addAboveRowButtonProps, setAddAboveRowButtonProps] = useState<
    ActionPopperButtonProps | undefined
  >(undefined)
  const [addBelowRowButtonProps, setAddBelowRowButtonProps] = useState<
    ActionPopperButtonProps | undefined
  >(undefined)
  const [addChildRowButtonProps, setAddChildRowButtonProps] = useState<
    ActionPopperAddChildButtonProps[] | undefined
  >(undefined)
  const [isWaitingForLoading, setIsWaitingForLoading] = useState<boolean>(false)

  const targetRowUuidRef = useRef<string | undefined>()
  const pendingActionRef = useRef<(() => boolean | undefined) | null>(null)
  const prevIsFetchingBody = usePrevious(isFetchingBody)

  useEffect(() => {
    if (!pendingActionRef.current) return
    if (prevIsFetchingBody && !isFetchingBody) {
      const result = pendingActionRef.current()
      if (result !== false) {
        pendingActionRef.current = null
        setIsWaitingForLoading(false)
      }
    }
  }, [isFetchingBody, prevIsFetchingBody])

  const getTargetRow = useCallback(() => {
    if (!gridApi || !targetRowUuidRef.current) return
    return gridApi.getRowNode(targetRowUuidRef.current)
  }, [gridApi, targetRowUuidRef])

  const getTargetRowHeight = useCallback(() => {
    const targetNode = getTargetRow()
    const height = targetNode?.rowHeight ?? undefined
    return height
  }, [getTargetRow])

  const getTargetRowData = useCallback(() => {
    const targetNode = getTargetRow()
    const row = targetNode?.data
    const wbsItem: NewWbsItemRow | undefined = row?.body?.wbsItem
    return {
      wbsItemType: wbsItem?.baseWbsItemType,
      ticketType: wbsItem?.ticketType,
      ticketListUuid:
        row.body?.ticketCumulation?.ticketListUuid ?? wbsItem?.ticketListUuid,
      targetNode,
    }
  }, [getTargetRow])

  const onClickButtonAddAbove = useCallback(() => {
    if (pendingActionRef.current) return

    const {
      wbsItemType: selectedRowType,
      ticketType,
      ticketListUuid,
    } = getTargetRowData()
    if (!selectedRowType) return

    const addRowType = selectedRowType.isTicket()
      ? store
          .getState()
          .project.ticketTypes.find(type => type.code === ticketType)
      : selectedRowType

    if (!addRowType) return

    const currentTargetUuid = targetRowUuidRef.current
    const executeAddAbove = () =>
      addRowAbove(
        addRowType,
        selectedRowType.isTicket() ? ticketListUuid : undefined,
        currentTargetUuid,
        false
      )

    const result = executeAddAbove()
    if (result === false) {
      setIsWaitingForLoading(true)
      pendingActionRef.current = executeAddAbove
    }
  }, [getTargetRowData, addRowAbove])

  const onClickButtonAddBelow = useCallback(() => {
    if (pendingActionRef.current) return

    const {
      wbsItemType: selectedRowType,
      ticketType,
      ticketListUuid,
      targetNode,
    } = getTargetRowData()
    if (!selectedRowType) return

    const addRowType = selectedRowType.isTicket()
      ? store
          .getState()
          .project.ticketTypes.find(type => type.code === ticketType)
      : selectedRowType

    if (!addRowType) return

    const currentTargetUuid = targetRowUuidRef.current
    const executeAddBelow = () => {
      const result = addRowBelow(
        addRowType,
        selectedRowType.isTicket() ? ticketListUuid : undefined,
        currentTargetUuid,
        false
      )
      if (result === true && targetNode && targetNode.expanded) {
        targetNode.setExpanded(false)
      }
      return result
    }

    const result = executeAddBelow()
    if (result === false) {
      setIsWaitingForLoading(true)
      pendingActionRef.current = executeAddBelow
    }
  }, [getTargetRowData, addRowBelow])

  const getAddRowTypeToChild = useCallback(
    (
      typeCode: string | undefined,
      selectedRowType: WbsItemTypeVO | undefined,
      ticketTypeCode: string | undefined
    ): WbsItemTypeVO | undefined => {
      if (!!typeCode) {
        const addType = [
          wbsItemTypes.task,
          wbsItemTypes.deliverable,
          wbsItemTypes.deliverableList,
          wbsItemTypes.process,
        ].filter(type => type.code === typeCode)

        if (!_.isEmpty(addType)) return addType[0]
      }
      if (!selectedRowType) return
      const addRowType =
        selectedRowType.isTicket() && selectedRowType.isDeliverable()
          ? store
              .getState()
              .project.ticketTypes.find(type => type.code === ticketTypeCode)
          : selectedRowType.isWorkgroup()
            ? wbsItemTypes.process
            : selectedRowType.isProcess()
              ? wbsItemTypes.deliverableList
              : selectedRowType.isDeliverableList()
                ? wbsItemTypes.deliverable
                : selectedRowType.isDeliverable()
                  ? wbsItemTypes.task
                  : selectedRowType.isTask()
                    ? wbsItemTypes.task
                    : undefined
      return addRowType
    },
    [wbsItemTypes]
  )

  const onClickButtonAddChild = useCallback(
    (addTypeCode?: string) => {
      const {
        wbsItemType: selectedRowType,
        ticketType,
        ticketListUuid,
      } = getTargetRowData()
      if (!selectedRowType) return

      const addRowType = getAddRowTypeToChild(
        addTypeCode,
        selectedRowType,
        ticketType
      )
      if (!addRowType) return
      addToChildRow(
        addRowType,
        selectedRowType.isTicket() && selectedRowType.isDeliverable()
          ? ticketListUuid
          : undefined,
        targetRowUuidRef.current
      )
      // To allow continuous clicking, the focus does not move.
    },
    [getTargetRowData, addToChildRow, getAddRowTypeToChild]
  )

  const open = useCallback(
    ({
      offsetX,
      offsetY,
      anchor,
      targetRowUuid,
      wbsItem,
      parentType,
    }: {
      offsetX: number
      offsetY: number
      anchor: HTMLElement
      targetRowUuid: string | undefined
      wbsItem?: NewWbsItemRow
      parentType?: WbsItemTypeVO
    }) => {
      setOffsetX(offsetX)
      setOffsetY(offsetY)
      setAnchor(anchor)
      targetRowUuidRef.current = targetRowUuid

      if (targetRowUuid) {
        prefetchParentBody(targetRowUuid)
      }

      const wbsItemType: WbsItemTypeVO | undefined = wbsItem?.baseWbsItemType
      const rootType: WbsItemType | undefined = wbsItemType?.rootType
      const isTicket: boolean = wbsItem?.wbsItemType.isTicket() ?? false

      // 親がworkgroupの場合、「上に追加」「下に追加」は親の子として
      // 同じ型を追加する処理のため、workgroup配下の子として追加不可能な
      // 型（成果物一覧・成果物・タスク等の既存データ）ではボタン自体を隠す。
      const isAddableAsSibling =
        !parentType ||
        !wbsItemType ||
        isAddableChildType(parentType, wbsItemType)

      const visibleAddRowButton =
        !(WbsItemType.DELIVERABLE === rootType && isTicket) &&
        isAddableAsSibling

      setAddAboveRowButtonProps(
        visibleAddRowButton
          ? ({
              onClick: onClickButtonAddAbove,
            } as ActionPopperButtonProps)
          : undefined
      )

      setAddBelowRowButtonProps(
        visibleAddRowButton
          ? ({
              onClick: onClickButtonAddBelow,
            } as ActionPopperButtonProps)
          : undefined
      )

      if (!isTicket && wbsItemType) {
        const candidateChildTypes = getAddableChildTypes(wbsItemType)

        const addableToChildTypes:
          | ActionPopperAddChildButtonProps[]
          | undefined = candidateChildTypes.map(
          type =>
            ({
              type: type.code,
              iconUrl: type.iconUrl,
              tooltipTitle: intl.formatMessage({
                id: `action.add.row.child.${fromSnakeToCamel(type.code)}`,
              }),
              onClick: () => onClickButtonAddChild(type.code),
            }) as ActionPopperAddChildButtonProps
        )
        setAddChildRowButtonProps(addableToChildTypes)
      } else {
        const isDeliverableList = WbsItemType.DELIVERABLE === rootType
        const iconUrl = isDeliverableList
          ? wbsItem!.wbsItemType.iconUrl
          : wbsItemTypes.task.iconUrl

        const tooltipTitle = isDeliverableList
          ? intl.formatMessage(
              {
                id: `action.add.row.child.ticket`,
              },
              {
                ticketType: wbsItemType?.name,
              }
            )
          : intl.formatMessage({
              id: `action.add.row.child.${fromSnakeToCamel(
                wbsItemTypes.task.code
              )}`,
            })

        setAddChildRowButtonProps([
          {
            iconUrl,
            tooltipTitle,
            onClick: () => onClickButtonAddChild(),
          },
        ])
      }
    },
    [
      wbsItemTypes,
      onClickButtonAddAbove,
      onClickButtonAddBelow,
      onClickButtonAddChild,
      prefetchParentBody,
    ]
  )

  const close = useCallback(() => {
    if (!!anchor) {
      setAnchor(undefined)
      setAddAboveRowButtonProps(undefined)
      setAddBelowRowButtonProps(undefined)
      setAddChildRowButtonProps(undefined)
    }
    pendingActionRef.current = null
    setIsWaitingForLoading(false)
  }, [anchor])

  const returnVal = useMemo(
    () => ({
      offsetX,
      offsetY,
      anchor,
      addAboveRowButtonProps,
      addBelowRowButtonProps,
      addChildRowButtonProps,
      targetRowUuid: targetRowUuidRef.current,
      open,
      close,
      isOpen: !!anchor,
      getTargetRowHeight,
      isWaitingForLoading,
    }),
    [
      offsetX,
      offsetY,
      anchor,
      addAboveRowButtonProps,
      addBelowRowButtonProps,
      addChildRowButtonProps,
      targetRowUuidRef,
      open,
      close,
      getTargetRowHeight,
      isWaitingForLoading,
    ]
  )
  return returnVal
}
